import {
  COOGO_EXECUTION_EVENT_VERSION,
  COOGO_ORCHESTRATION_PLAN_V2_VERSION,
  COOGO_ORCHESTRATION_PLAN_VERSION,
  validateCoogoExecutionEvent,
  validateCoogoOrchestrationPlan,
  type CoogoExecutionEvent,
  type CoogoExecutionEventType,
  type CoogoOrchestrationPlan,
  type CoogoOrchestrationPlanV2,
  type CoogoOrchestrationTrigger,
} from '@cogo/webmcp-runtime'

import { classifyStudioExactImpactTarget } from './studio-exact-impact-readback'

interface StudioOrchestrationCommandInput {
  tool: string
  params: Record<string, unknown>
  expectedChangedPaths?: string[]
  exactChanges?: Array<{
    path: string
    label: string
    before?: unknown
    proposed?: unknown
  }>
  requiresConfirmation?: boolean
  readback?: {
    tool: string
    params?: Record<string, unknown>
  }
  sourceCallId?: string
}

interface StudioOrchestrationPlanInput {
  planId: string
  label?: string
  runId?: string
  parentRunId?: string
  source: 'chat-intent-chunk' | 'chat-operation' | 'webmcp' | 'studio-ui'
  commands: StudioOrchestrationCommandInput[]
}

export interface StudioStep5PageRenameAdmission {
  authenticated: boolean
  durability: 'durable' | 'session-only'
  confirmationId: string
  confirmationPhrase: string
  expiresAt: string
}

interface StudioGenericOrchestrationCompilerInput {
  plan: StudioOrchestrationPlanInput
  projectId: string | null
  isReadOnly: (command: StudioOrchestrationCommandInput) => boolean
}

interface StudioStep5PageRenameCompilerInput extends StudioGenericOrchestrationCompilerInput {
  mode: 'step5-page-title-rename'
  admission: StudioStep5PageRenameAdmission
  createdAt?: string
}

export interface StudioOrchestrationReference {
  planSchemaVersion: typeof COOGO_ORCHESTRATION_PLAN_VERSION
  eventSchemaVersion: typeof COOGO_EXECUTION_EVENT_VERSION
  runId: string
  parentRunId?: string
  nodeIds: string[]
}

const TRIGGER_BY_SOURCE: Record<
  StudioOrchestrationPlanInput['source'],
  CoogoOrchestrationTrigger
> = {
  'chat-intent-chunk': 'chat',
  'chat-operation': 'chat',
  webmcp: 'webmcp',
  'studio-ui': 'studio-ui',
}

const EXACT_IMPACT_READBACK_TOOL = 'cogo.studio.capability.readbackExactImpact'
const UPDATE_PAGE_META_TOOL = 'cogo.studio.updatePageMeta'
const UNDO_TOOL = 'cogo.studio.undo'

function rejectStep5PageRename(reason: string): never {
  throw new Error(`Step 5 page rename preflight rejected: ${reason}`)
}

function sameArray(left: readonly string[], right: readonly string[]): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  )
}

function pageRenameCommand(input: StudioStep5PageRenameCompilerInput): {
  pageId: string
  title: string
  path: string
  createdAt: string
} {
  if (!input.projectId?.trim()) {
    rejectStep5PageRename('an authenticated project is required.')
  }
  if (input.plan.commands.length !== 1) {
    rejectStep5PageRename('exactly one command is required.')
  }
  const command = input.plan.commands[0]
  if (command.tool !== UPDATE_PAGE_META_TOOL) {
    rejectStep5PageRename(`unsupported tool ${command.tool}.`)
  }
  const rawPageId = command.params.pageId
  if (typeof rawPageId !== 'string' || !rawPageId.trim()) {
    rejectStep5PageRename('pageId is required.')
  }
  if (rawPageId !== rawPageId.trim()) {
    rejectStep5PageRename(
      'pageId must use canonical bytes without outer whitespace.',
    )
  }
  const pageId = rawPageId
  const title =
    typeof command.params.title === 'string' ? command.params.title : ''
  if (!title.trim()) rejectStep5PageRename('a non-empty title is required.')
  const unsupportedParams = Object.keys(command.params).filter(
    (key) => key !== 'pageId' && key !== 'title',
  )
  if (unsupportedParams.length > 0) {
    rejectStep5PageRename(
      `only the title field may change; found ${unsupportedParams.join(', ')}.`,
    )
  }
  if (!input.admission.authenticated) {
    rejectStep5PageRename('verified authentication is required.')
  }
  if (input.admission.durability !== 'durable') {
    rejectStep5PageRename('session-only admission cannot authorize execution.')
  }
  if (!input.admission.confirmationId.trim()) {
    rejectStep5PageRename('confirmationId is required.')
  }
  if (!input.admission.confirmationPhrase.trim()) {
    rejectStep5PageRename('typed confirmation is required.')
  }
  const createdAt = input.createdAt ?? new Date().toISOString()
  if (
    Number.isNaN(Date.parse(createdAt)) ||
    Number.isNaN(Date.parse(input.admission.expiresAt)) ||
    Date.parse(input.admission.expiresAt) <= Date.parse(createdAt)
  ) {
    rejectStep5PageRename('the durable admission expiry is invalid.')
  }

  const path = `/manifest/pages/${encodeURIComponent(pageId)}/title`
  try {
    const target = classifyStudioExactImpactTarget(path)
    if (target.kind !== 'page-title' || target.pageId !== pageId) {
      rejectStep5PageRename('the exact page-title path is unsupported.')
    }
  } catch {
    rejectStep5PageRename('the exact page-title path is unsupported.')
  }
  if (
    !sameArray(command.expectedChangedPaths ?? [], [path]) ||
    command.exactChanges?.length !== 1 ||
    command.exactChanges[0]?.path !== path ||
    command.exactChanges[0]?.proposed !== title
  ) {
    rejectStep5PageRename(
      'the exact impact must name only the target page title.',
    )
  }
  if (command.readback !== undefined) {
    rejectStep5PageRename(
      'caller-supplied readback is not allowed; the graph owns exact readback.',
    )
  }
  if (command.requiresConfirmation === false) {
    rejectStep5PageRename('the mutation requires typed human confirmation.')
  }
  return { pageId, title, path, createdAt }
}

function compileStep5PageRenamePlan(
  input: StudioStep5PageRenameCompilerInput,
): CoogoOrchestrationPlanV2 {
  const { pageId, title, path, createdAt } = pageRenameCommand(input)
  const projectId = input.projectId as string
  const runId = input.plan.runId ?? crypto.randomUUID()
  const readbackPolicy = {
    authority: 'studio' as const,
    readOnly: true,
    confirmation: 'none' as const,
    retry: 'safe' as const,
    timeoutMs: 10_000,
  }
  const compensationInput = {
    compensation: {
      preReadback: {
        operation: EXACT_IMPACT_READBACK_TOOL,
        path,
        beforeSourceNodeId: 'n1-impact-before',
        proposed: title,
      },
      exactBefore: 'already-restored',
      exactProposed: 'owned-undo',
      requiresOwnershipReceipt: true,
      requiresLiveStackTop: true,
      ownershipSourceNodeId: 'n4-update-page-meta',
      otherwise: 'manual-recovery',
    },
  }
  const plan: CoogoOrchestrationPlanV2 = {
    schemaVersion: COOGO_ORCHESTRATION_PLAN_V2_VERSION,
    planId: input.plan.planId,
    ...(input.plan.label ? { label: input.plan.label } : {}),
    runId,
    ...(input.plan.parentRunId ? { parentRunId: input.plan.parentRunId } : {}),
    projectId,
    trigger: TRIGGER_BY_SOURCE[input.plan.source],
    createdAt,
    nodes: [
      {
        id: 'n1-impact-before',
        kind: 'readback',
        operation: {
          name: EXACT_IMPACT_READBACK_TOOL,
          params: { mode: 'capture', paths: [path] },
        },
        input: { exactImpact: { mode: 'capture', path } },
        policy: readbackPolicy,
      },
      {
        id: 'n2-human-gate',
        kind: 'gate',
        dependsOn: ['n1-impact-before'],
        humanGate: {
          confirmationId: input.admission.confirmationId,
          expiresAt: input.admission.expiresAt,
          principalBinding: 'authenticated-owner',
          sessionBinding: 'verified-jwt-session',
          target: { kind: 'studio-page', projectId, pageId },
          duplicatePolicy: 'reject',
          durability: 'durable',
          confirmationPhrase: input.admission.confirmationPhrase,
        },
        input: {
          staleRevalidation: {
            sourceNodeId: 'n1-impact-before',
            operation: EXACT_IMPACT_READBACK_TOOL,
            path,
            mismatchCode: 'CONFIRMATION_STALE',
          },
        },
        policy: {
          authority: 'human',
          readOnly: true,
          confirmation: 'typed',
          retry: 'never',
          timeoutMs: 600_000,
        },
      },
      {
        id: 'n4-update-page-meta',
        kind: 'tool',
        dependsOn: ['n2-human-gate'],
        operation: {
          name: UPDATE_PAGE_META_TOOL,
          params: { pageId, title },
        },
        input: {
          approvalGrant: {
            policy: 'payload-bound-single-use',
            confirmationNodeId: 'n2-human-gate',
          },
        },
        policy: {
          authority: 'studio',
          readOnly: false,
          confirmation: 'policy',
          retry: 'never',
          timeoutMs: 30_000,
          idempotencyKey: `${projectId}:${runId}:n4-update-page-meta`,
        },
      },
      {
        id: 'n5-impact-after',
        kind: 'readback',
        dependsOn: ['n4-update-page-meta'],
        operation: {
          name: EXACT_IMPACT_READBACK_TOOL,
          params: {
            mode: 'assert',
            checks: [{ path, expectation: 'equals', expected: title }],
          },
        },
        input: {
          exactImpact: { mode: 'assert-proposed', path, proposed: title },
        },
        policy: readbackPolicy,
      },
      {
        id: 'r1-undo-n4-failure',
        kind: 'tool',
        dependsOn: [
          'n1-impact-before',
          { nodeId: 'n4-update-page-meta', when: 'failed' },
        ],
        operation: { name: UNDO_TOOL },
        input: structuredClone(compensationInput),
        policy: {
          authority: 'studio',
          readOnly: false,
          confirmation: 'policy',
          retry: 'never',
          timeoutMs: 30_000,
          idempotencyKey: `${projectId}:${runId}:r1-undo-n4-failure`,
        },
      },
      {
        id: 'r2-verify-n4-restore',
        kind: 'readback',
        dependsOn: ['r1-undo-n4-failure'],
        operation: { name: EXACT_IMPACT_READBACK_TOOL },
        input: {
          exactImpact: {
            mode: 'assert-source',
            path,
            sourceNodeId: 'n1-impact-before',
          },
        },
        policy: readbackPolicy,
      },
      {
        id: 'r1-undo-n5-failure',
        kind: 'tool',
        dependsOn: [
          'n1-impact-before',
          'n4-update-page-meta',
          { nodeId: 'n5-impact-after', when: 'failed' },
        ],
        operation: { name: UNDO_TOOL },
        input: structuredClone(compensationInput),
        policy: {
          authority: 'studio',
          readOnly: false,
          confirmation: 'policy',
          retry: 'never',
          timeoutMs: 30_000,
          idempotencyKey: `${projectId}:${runId}:r1-undo-n5-failure`,
        },
      },
      {
        id: 'r2-verify-n5-restore',
        kind: 'readback',
        dependsOn: ['r1-undo-n5-failure'],
        operation: { name: EXACT_IMPACT_READBACK_TOOL },
        input: {
          exactImpact: {
            mode: 'assert-source',
            path,
            sourceNodeId: 'n1-impact-before',
          },
        },
        policy: readbackPolicy,
      },
    ],
  }
  const validation = validateCoogoOrchestrationPlan(plan)
  if (!validation.valid) {
    throw new Error(
      `Invalid Studio orchestration plan: ${validation.errors.join('; ')}`,
    )
  }
  return plan
}

export function compileStudioOrchestrationPlan(
  input: StudioStep5PageRenameCompilerInput,
): CoogoOrchestrationPlanV2
export function compileStudioOrchestrationPlan(
  input: StudioGenericOrchestrationCompilerInput,
): CoogoOrchestrationPlan
export function compileStudioOrchestrationPlan(
  input:
    | StudioStep5PageRenameCompilerInput
    | StudioGenericOrchestrationCompilerInput,
): CoogoOrchestrationPlan | CoogoOrchestrationPlanV2 {
  const requestedMode = (input as { mode?: unknown }).mode
  if (requestedMode !== undefined) {
    if (requestedMode !== 'step5-page-title-rename') {
      rejectStep5PageRename('the bounded compiler mode is unsupported.')
    }
    return compileStep5PageRenamePlan(
      input as StudioStep5PageRenameCompilerInput,
    )
  }
  if (!input.projectId) {
    throw new Error('Studio orchestration requires a loaded project.')
  }
  const runId = input.plan.runId ?? crypto.randomUUID()
  const plan: CoogoOrchestrationPlan = {
    schemaVersion: COOGO_ORCHESTRATION_PLAN_VERSION,
    planId: input.plan.planId,
    ...(input.plan.label ? { label: input.plan.label } : {}),
    runId,
    ...(input.plan.parentRunId ? { parentRunId: input.plan.parentRunId } : {}),
    projectId: input.projectId,
    trigger: TRIGGER_BY_SOURCE[input.plan.source],
    createdAt: new Date().toISOString(),
    nodes: input.plan.commands.map((command, index) => {
      const readOnly = input.isReadOnly(command)
      const executionInput = {
        ...(command.expectedChangedPaths
          ? {
              expectedChangedPaths: structuredClone(
                command.expectedChangedPaths,
              ),
            }
          : {}),
        ...(command.exactChanges
          ? { exactChanges: structuredClone(command.exactChanges) }
          : {}),
        ...(command.readback
          ? { readback: structuredClone(command.readback) }
          : {}),
        ...(command.sourceCallId ? { sourceCallId: command.sourceCallId } : {}),
      }
      return {
        id: `command-${index + 1}`,
        kind: 'tool' as const,
        ...(index > 0 ? { dependsOn: [`command-${index}`] } : {}),
        operation: {
          name: command.tool,
          params: structuredClone(command.params),
        },
        ...(Object.keys(executionInput).length > 0
          ? { input: executionInput }
          : {}),
        policy: {
          authority: 'studio' as const,
          readOnly,
          confirmation: readOnly ? ('none' as const) : ('user' as const),
          retry: 'never' as const,
          timeoutMs: 30_000,
          ...(!readOnly
            ? {
                idempotencyKey: [
                  input.projectId,
                  runId,
                  input.plan.planId,
                  index + 1,
                ].join(':'),
              }
            : {}),
        },
      }
    }),
  }
  const validation = validateCoogoOrchestrationPlan(plan)
  if (!validation.valid) {
    throw new Error(
      `Invalid Studio orchestration plan: ${validation.errors.join('; ')}`,
    )
  }
  return plan
}

export function studioOrchestrationReference(
  plan: CoogoOrchestrationPlan,
): StudioOrchestrationReference {
  return {
    planSchemaVersion: COOGO_ORCHESTRATION_PLAN_VERSION,
    eventSchemaVersion: COOGO_EXECUTION_EVENT_VERSION,
    runId: plan.runId,
    ...(plan.parentRunId ? { parentRunId: plan.parentRunId } : {}),
    nodeIds: plan.nodes.map((node) => node.id),
  }
}

export function appendStudioOrchestrationEvent(input: {
  events: CoogoExecutionEvent[]
  plan: CoogoOrchestrationPlan
  type: CoogoExecutionEventType
  nodeId?: string
  data?: Record<string, unknown>
}): CoogoExecutionEvent {
  const event: CoogoExecutionEvent = {
    schemaVersion: COOGO_EXECUTION_EVENT_VERSION,
    eventId: crypto.randomUUID(),
    planId: input.plan.planId,
    runId: input.plan.runId,
    projectId: input.plan.projectId,
    sequence: input.events.length,
    occurredAt: new Date().toISOString(),
    type: input.type,
    ...(input.nodeId ? { nodeId: input.nodeId } : {}),
    ...(input.data ? { data: input.data } : {}),
  }
  const validation = validateCoogoExecutionEvent(event)
  if (!validation.valid) {
    throw new Error(
      `Invalid Studio orchestration event: ${validation.errors.join('; ')}`,
    )
  }
  input.events.push(event)
  return event
}
